import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { Zap, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { getSharedChat } from "@/lib/api";

export default function SharedChat() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getSharedChat(slug)
      .then(setData)
      .catch((e) =>
        setError(e?.response?.status === 404 ? "not_found" : "error")
      );
  }, [slug]);

  if (error === "not_found") return <NotFound />;
  if (error) return <NotFound title="Something went wrong" msg="Please try again in a moment." />;
  if (!data)
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );

  return (
    <div className="min-h-screen bg-background text-foreground font-sans" data-testid="shared-chat-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border bg-white/70 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 md:px-6 h-14">
          <Link to="/" className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-heading text-sm tracking-tight">chatepc<span className="text-epc-a">.co.uk</span></span>
          </Link>
          <Link to="/chat" data-testid="shared-start-own">
            <Button size="sm" className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
              Start your own chat <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-epc-a" /> Shared chat · read-only
        </div>
        <h1 className="mt-2 font-heading text-2xl md:text-3xl tracking-tight" data-testid="shared-title">
          {data.title}
        </h1>
        <div className="mt-1 text-xs text-muted-foreground">
          {new Date(data.updated_at).toLocaleString("en-GB")}
        </div>

        <ul className="mt-8 space-y-6">
          {data.messages.map((m, i) => (
            <Bubble key={i} role={m.role} content={m.content} />
          ))}
        </ul>

        <div className="mt-12 border border-border p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div>
            <div className="font-heading text-lg tracking-tight">Have your own EPC question?</div>
            <div className="text-sm text-muted-foreground mt-1">
              Start a fresh chat with ChatEPC — free, no sign-up.
            </div>
          </div>
          <Link to="/chat" data-testid="shared-footer-start">
            <Button className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
              Open ChatEPC <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </main>

      <footer className="border-t border-border mt-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 text-xs text-muted-foreground flex flex-wrap justify-between gap-2">
          <span>chatepc.co.uk · specialist AI for UK EPCs</span>
          <span>Read-only shared conversation</span>
        </div>
      </footer>
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <li
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`shared-msg-${role}`}
    >
      <div
        className={`max-w-[85%] px-4 py-3 leading-relaxed text-[15px] ${
          isUser
            ? "bg-epc-a text-white"
            : "bg-neutral-100 text-foreground border border-border"
        }`}
      >
        {isUser ? (
          <div className="msg-body whitespace-pre-wrap">{content}</div>
        ) : (
          <div className="msg-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-epc-a underline underline-offset-2 hover:text-epc-a/80 break-words"
                  />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </li>
  );
}

function NotFound({ title = "Shared chat not found", msg = "The link may have been disabled by its owner." }) {
  return (
    <div className="min-h-screen grid place-items-center px-6 text-center">
      <div className="max-w-md">
        <AlertTriangle className="h-8 w-8 text-epc-f mx-auto" />
        <h1 className="mt-4 font-heading text-2xl tracking-tight">{title}</h1>
        <p className="mt-2 text-muted-foreground text-sm">{msg}</p>
        <Link to="/chat" className="inline-block mt-6">
          <Button className="rounded-none bg-epc-a hover:bg-epc-a/90 text-white">
            Start your own chat
          </Button>
        </Link>
      </div>
    </div>
  );
}
